import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { supabase } from '../lib/supabaseClient';
import './Reports.css';

// Human-friendly duration, e.g. 45s · 12m · 2h 15m · 3d 4h.
function formatDuration(ms) {
  if (ms == null || !isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) {
    const rm = m % 60;
    return rm ? `${h}h ${rm}m` : `${h}h`;
  }
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

function median(nums) {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export default function Reports() {
  const [calls, setCalls] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [callsRes, staffRes] = await Promise.all([
      supabase
        .from('calls')
        .select('id, assignee_id, status, started_at, created_at, resolved_at')
        .limit(2000),
      supabase.from('profiles').select('id, full_name, email, role'),
    ]);
    setCalls(callsRes.data || []);
    setStaff(staffRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the report current as calls get assigned / resolved.
  useEffect(() => {
    const channel = supabase
      .channel('reports-stream')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'calls' }, (payload) => {
        setCalls((prev) => {
          if (payload.eventType === 'INSERT') {
            if (prev.some((c) => c.id === payload.new.id)) return prev;
            return [payload.new, ...prev];
          }
          if (payload.eventType === 'UPDATE') {
            return prev.map((c) => (c.id === payload.new.id ? { ...c, ...payload.new } : c));
          }
          if (payload.eventType === 'DELETE') {
            return prev.filter((c) => c.id !== payload.old.id);
          }
          return prev;
        });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Everyone with a login can be assigned calls — admins included.
  const reps = useMemo(
    () =>
      [...staff].sort((a, b) =>
        (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')
      ),
    [staff]
  );

  const stats = useMemo(() => {
    const durationOf = (c) => {
      const start = c.started_at || c.created_at;
      if (!c.resolved_at || !start) return null;
      const d = new Date(c.resolved_at) - new Date(start);
      return d >= 0 ? d : null;
    };

    const build = (list) => {
      const resolved = list.filter((c) => c.status === 'resolved');
      const durations = resolved.map(durationOf).filter((d) => d != null);
      return {
        jobs: list.length,
        open: list.length - resolved.length,
        resolved: resolved.length,
        avg: durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : null,
        median: median(durations),
        fastest: durations.length ? Math.min(...durations) : null,
        measured: durations.length,
      };
    };

    const rows = reps
      .map((rep) => ({ rep, ...build(calls.filter((c) => c.assignee_id === rep.id)) }))
      .sort((a, b) => b.jobs - a.jobs);

    const unassigned = build(calls.filter((c) => !c.assignee_id));
    const overall = build(calls);
    const maxJobs = Math.max(1, ...rows.map((r) => r.jobs));

    return { rows, unassigned, overall, maxJobs };
  }, [calls, reps]);

  const name = (rep) => rep.full_name || rep.email;

  return (
    <AdminLayout
      title="Reports"
      actions={
        <button className="btn btn-ghost sub-nav-refresh" onClick={load}>
          Refresh
        </button>
      }
    >
      {loading ? (
        <div className="board-empty">Loading…</div>
      ) : (
        <>
          {/* Top-line numbers */}
          <div className="report-cards">
            <div className="report-card">
              <span className="rc-label">Total jobs</span>
              <span className="rc-value">{stats.overall.jobs}</span>
            </div>
            <div className="report-card">
              <span className="rc-label">Resolved</span>
              <span className="rc-value">{stats.overall.resolved}</span>
            </div>
            <div className="report-card">
              <span className="rc-label">Still open</span>
              <span className="rc-value">{stats.overall.open}</span>
            </div>
            <div className="report-card">
              <span className="rc-label">Avg time to resolve</span>
              <span className="rc-value">{formatDuration(stats.overall.avg)}</span>
            </div>
          </div>

          {/* Per-rep tracker */}
          <div className="ui-table-wrap report-table-wrap">
            <table className="ui-table report-table">
              <thead>
                <tr>
                  <th>Rep</th>
                  <th>Jobs</th>
                  <th>Open</th>
                  <th>Resolved</th>
                  <th>Avg to resolve</th>
                  <th>Fastest</th>
                </tr>
              </thead>
              <tbody>
                {stats.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="ui-empty">
                      No reps yet. Create staff logins under People, then assign calls to them.
                    </td>
                  </tr>
                )}
                {stats.rows.map(({ rep, jobs, open, resolved, avg, median: med, fastest, measured }) => (
                  <tr key={rep.id} className="ui-row">
                    <td className="rep-name">{name(rep)}</td>
                    <td>
                      <div className="jobs-cell">
                        <span className="jobs-num">{jobs}</span>
                        <span className="jobs-bar">
                          <span
                            className="jobs-bar-fill"
                            style={{ width: `${(jobs / stats.maxJobs) * 100}%` }}
                          />
                        </span>
                      </div>
                    </td>
                    <td>{open}</td>
                    <td>{resolved}</td>
                    <td>
                      <span className="dur-main">{formatDuration(avg)}</span>
                      {measured > 0 && med != null && avg != null && med !== avg && (
                        <span className="dur-sub">median {formatDuration(med)}</span>
                      )}
                    </td>
                    <td className="muted">{formatDuration(fastest)}</td>
                  </tr>
                ))}
                {stats.unassigned.jobs > 0 && (
                  <tr className="ui-row unassigned-row">
                    <td className="rep-name muted">Unassigned</td>
                    <td>
                      <div className="jobs-cell">
                        <span className="jobs-num">{stats.unassigned.jobs}</span>
                      </div>
                    </td>
                    <td>{stats.unassigned.open}</td>
                    <td>{stats.unassigned.resolved}</td>
                    <td className="muted">—</td>
                    <td className="muted">—</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="report-note">
            “Jobs” counts every call routed to a rep. “Time to resolve” is measured from when the
            call came in to when the rep marked it Resolved, and only counts calls resolved from now
            on.
          </p>
        </>
      )}
    </AdminLayout>
  );
}
